import React from 'react';
import { GitBranch, ChevronRight, ChevronDown, FileBarChart } from 'lucide-react';
import { Button } from '../ui/button';
import { getStatusInfo } from '../../pages/planner/constants';
import { fmtQty, partsSent, showDate } from './phasedUtils';

const PartNode = ({ part, current, onOpen, last }) => {
  const st = getStatusInfo(part.status);
  return (
    <li className="relative pl-5 py-0.5" data-testid={`po-tree-part-${part.dispatch_number}`}>
      <span className={`absolute left-0 top-0 border-l border-primary/30 ${last ? 'h-1/2' : 'h-full'}`} />
      <span className="absolute left-0 top-1/2 w-4 border-t border-primary/30" />
      <button
        type="button"
        onClick={() => onOpen?.(part)}
        disabled={!onOpen || current}
        className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
          current ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-white'
        }`}
        data-testid={`po-tree-open-${part.dispatch_number}`}
      >
        <span className="font-mono font-semibold text-primary">#{part.dispatch_number}</span>
        <span className="font-medium">Part {part.part_number}</span>
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${st.color}`}>{st.label}</span>
        <span className="font-mono text-muted-foreground">{showDate(part.dispatch_date)}</span>
        <span className="font-mono">{fmtQty(part.units)} units</span>
        {part.part_origin === 'balance' && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">balance</span>}
        {current && <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-primary">this order</span>}
      </button>
    </li>
  );
};

export const PoPartsTree = ({ order, onOpenPart, onOpenSummary }) => {
  const parts = order?.po_parts || [];
  if (!parts.length) return null;
  return (
    <div className="rounded-lg border border-primary/20 bg-primary/[0.03] p-3" data-testid={`po-tree-${order.id}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <GitBranch className="h-3.5 w-3.5 text-primary" />
          <span className="font-mono font-semibold">PO {order.po_number || '—'}</span>
          <span className="text-muted-foreground" data-testid={`po-tree-count-${order.id}`}>
            {parts.length} linked dispatch notes · {partsSent(parts)} sent
          </span>
          {order.po_original_order && (
            <span className="text-muted-foreground">· original order #{order.po_original_order}</span>
          )}
        </div>
        {onOpenSummary && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onOpenSummary(order)} data-testid={`po-summary-btn-${order.id}`}>
            <FileBarChart className="mr-1 h-3.5 w-3.5" /> PO summary
          </Button>
        )}
      </div>
      <ul className="ml-2">
        {parts.map((p, i) => (
          <PartNode key={p.id} part={p} current={p.id === order.id} onOpen={onOpenPart} last={i === parts.length - 1} />
        ))}
      </ul>
    </div>
  );
};

export const PoPartChip = ({ dispatch, expanded, onToggle }) => {
  const parts = dispatch.po_parts || [];
  const Icon = expanded ? ChevronDown : ChevronRight;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className="mt-1 inline-flex items-center gap-1 rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/10"
      title="Show the linked dispatch notes for this PO"
      data-testid={`po-part-chip-${dispatch.id}`}
    >
      <Icon className="h-3 w-3" />
      <GitBranch className="h-2.5 w-2.5" />
      <span>{dispatch.fulfilment_label}</span>
      <span className="text-primary/70">· {parts.length} notes</span>
    </button>
  );
};
